/*
 * "Hide email images": this person's choice for one workspace, kept by the server (rendering plan,
 * slice 3: shown by default, a personal workspace-scoped block). It sits beside Dark mode, where the
 * account menu and the phone's menu sheet keep what the reader sees. It appears only where the server
 * offers the preference; until then the default, show, holds and nothing here is drawn.
 *
 * One email can still show its images (`mail-frame.tsx`, "Show in this email") without changing it.
 */
import { CheckIcon, ImageOffIcon } from "../icons.tsx";
import { useImagePreference, useSetImagePreference } from "./use-message-images.ts";

type Props = { workspaceId: string; workspaceName: string };

function usePreferenceSwitch(workspaceId: string) {
  const { preference, settled } = useImagePreference(workspaceId);
  const set = useSetImagePreference(workspaceId);
  return {
    shown: settled && preference.available,
    hidden: preference.blockExternal,
    toggle: () => void set(!preference.blockExternal),
  };
}

/** The account menu's row, a checkbox like Dark mode. */
export function ImagePreferenceMenuItem(props: Props) {
  const choice = usePreferenceSwitch(props.workspaceId);
  if (!choice.shown) return null;
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={choice.hidden}
      className="lcr-menu-item lcr-rail-account-item"
      onClick={choice.toggle}
    >
      <ImageOffIcon size={18} />
      <span className="lcr-menu-item-text">
        <span className="lcr-menu-item-label">Hide email images</span>
        <span className="lcr-menu-item-detail">In {props.workspaceName}</span>
      </span>
      {choice.hidden && <CheckIcon size={16} />}
    </button>
  );
}

/** The phone menu sheet's row, a switch like Dark mode. */
export function ImagePreferenceTrayRow(props: Props) {
  const choice = usePreferenceSwitch(props.workspaceId);
  if (!choice.shown) return null;
  return (
    <section className="lcr-tray-section" aria-label="Email display">
      <div className="lcr-tray-list">
        <button
          type="button"
          className="lcr-tray-row"
          role="switch"
          aria-checked={choice.hidden}
          onClick={choice.toggle}
        >
          <ImageOffIcon size={20} />
          <span className="lcr-tray-row-text lcr-menu-item-text">
            <span className="lcr-menu-item-label">Hide email images</span>
            <span className="lcr-menu-item-detail">In {props.workspaceName}</span>
          </span>
          <span className="lcr-tray-switch" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
